/**
 * "Run it in Purview for me" (sc-11767): RedactIt creates the search in the
 * Purview case, runs it, exports the hits when the count is in, and imports the
 * export into this case. Nothing to do in Purview, and no export to come back
 * for: the case's documents panel shows the progress.
 *
 * Shown in the Purview search builder when Purview is connected for the tenant.
 * The manual steps (copy the KeyQL, run and export it in Purview) stay below it.
 */
import { useEffect, useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2, Play, X } from 'lucide-react';
import { Button } from '../ui/Button';
import { Label } from '../ui/label';
import { SearchableDropdown } from '../ui/SearchableDropdown';
import { cn } from '../ui/utils';
import { apiHelpers, intakeApi } from '../../lib/api';
import type { EDiscoveryCaseSummary, EDiscoverySearchScope, EDiscoverySource } from '../../types';
import { runBlocker, type PurviewRunCase } from './purviewSearch';

export type { PurviewRunCase } from './purviewSearch';

const SEARCH_DEBOUNCE_MS = 250;

export interface PurviewRunPanelProps {
  caseId: string;
  /** The KeyQL to run (the builder's current query). */
  query: string;
  /** The builder flags an error in the query (over Purview's limits). */
  queryBlocked?: boolean;
  /** The mailboxes and sites typed in the builder's data sources. */
  listedSources: string[];
  /** The case picked in Data collection, when there is one. */
  initialCase?: PurviewRunCase | null;
  /** 'wizard': the case opens next. 'case': the case page shows the progress. */
  context: 'wizard' | 'case';
  /** The run started; the source is what the server made of it. */
  onStarted?: (source: EDiscoverySource) => void;
}

const SCOPE_LABELS: Record<EDiscoverySearchScope, string> = {
  listed: 'The data sources listed here',
  tenant: 'Every mailbox and SharePoint site',
  case: 'The Purview case’s data sources',
};

export function PurviewRunPanel({
  caseId, query, queryBlocked, listedSources, initialCase, context, onStarted,
}: PurviewRunPanelProps) {
  const [purviewCase, setPurviewCase] = useState<PurviewRunCase | null>(initialCase ?? null);
  const [chosenScope, setChosenScope] = useState<EDiscoverySearchScope | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [started, setStarted] = useState<EDiscoverySource | null>(null);

  // Follow the case picked in Data collection until one is chosen here. Keyed
  // on the id: the parent passes a new object on every render.
  const choseHere = useRef(false);
  const initialId = initialCase?.externalId;
  const initialName = initialCase?.name;
  useEffect(() => {
    if (!choseHere.current && initialId && initialName) setPurviewCase({ externalId: initialId, name: initialName });
  }, [initialId, initialName]);

  // The listed sources by default, once there are some; every mailbox and site otherwise.
  const scope: EDiscoverySearchScope = chosenScope ?? (listedSources.length > 0 ? 'listed' : 'tenant');
  const blocker = runBlocker({ purviewCase, query, queryBlocked, scope, listedSources });

  // A different query, case or scope is a different run.
  const caseId_ = purviewCase?.externalId;
  useEffect(() => {
    setStarted(null);
    setError(null);
  }, [query, caseId_, scope]);

  const run = async () => {
    if (blocker || !purviewCase) return;
    setRunning(true);
    setError(null);
    try {
      const source = await intakeApi.runEDiscoverySearch(caseId, {
        system: 'purview',
        externalCaseId: purviewCase.externalId,
        query,
        scope,
        sources: scope === 'listed' ? listedSources : [],
      });
      setStarted(source);
      onStarted?.(source);
    } catch (err) {
      setError(apiHelpers.errorDetail(err) || 'Could not start the search in Purview. Try again in a moment.');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-3 rounded-lg border border-primary/40 bg-[#f9f5ff] p-3.5">
      <div className="space-y-1">
        <p className="flex items-center gap-2 text-[13px] font-semibold text-gray-900">
          <Play className="h-4 w-4 text-primary" /> Run it in Purview for me
        </p>
        <p className="text-xs leading-relaxed text-gray-600">
          RedactIt creates this search in the Purview case, runs it, exports what it finds and adds the files to this
          case. You don&apos;t need to open Purview or come back for the export.
        </p>
      </div>

      <PurviewCaseField value={purviewCase} onChange={(c) => { choseHere.current = true; setPurviewCase(c); }} />

      <fieldset className="space-y-1.5">
        <legend className="mb-1 text-xs font-medium text-gray-700">Search in</legend>
        {(['listed', 'tenant', 'case'] as const).map((value) => {
          const id = `purview-run-scope-${value}`;
          const disabled = value === 'listed' && listedSources.length === 0;
          return (
            <label key={value} htmlFor={id}
              className={cn('flex items-start gap-2 text-xs text-gray-800', disabled ? 'opacity-50' : 'cursor-pointer')}>
              <input id={id} type="radio" name="purview-run-scope" value={value} checked={scope === value}
                disabled={disabled} onChange={() => setChosenScope(value)} className="mt-0.5 accent-primary" />
              <span>
                {SCOPE_LABELS[value]}
                {value === 'listed' && listedSources.length > 0 && ` (${listedSources.length})`}
                {value === 'listed' && listedSources.length === 0 && ' (none listed yet)'}
              </span>
            </label>
          );
        })}
      </fieldset>

      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={run} disabled={running || !!blocker || !!started}>
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
          {running ? 'Creating the search…' : 'Run in Purview'}
        </Button>
        {running && <span className="text-xs text-gray-500">Purview takes up to a minute to set up the search.</span>}
        {blocker && !started && !running && <span className="text-xs text-gray-500">{blocker}</span>}
      </div>

      {error && (
        <p className="flex items-start gap-1 text-xs text-rose-600" role="alert">
          <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" /> {error}
        </p>
      )}
      {started && (
        <p className="flex items-start gap-1.5 text-xs leading-relaxed text-green-800" role="status">
          <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            Running in Purview. The files are added to this case when the export finishes, from a few minutes to a few
            hours.{' '}
            {context === 'wizard'
              ? 'Open the case whenever you like: its documents panel shows the progress.'
              : 'The documents panel shows the progress; you can close this.'}
          </span>
        </p>
      )}
    </div>
  );
}

/** The Purview case to run in: the picked one, or a searchable list of the tenant's cases. */
function PurviewCaseField({ value, onChange }: { value: PurviewRunCase | null; onChange: (c: PurviewRunCase | null) => void }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<EDiscoveryCaseSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);

  useEffect(() => {
    if (value) return;
    const mine = ++seq.current;
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const cases = await intakeApi.searchEDiscoveryCases('purview', query);
        if (mine === seq.current) {
          setResults(cases);
          setError(null);
        }
      } catch (err) {
        if (mine === seq.current) setError(apiHelpers.errorDetail(err) || 'Could not list the Purview cases');
      } finally {
        if (mine === seq.current) setLoading(false);
      }
    }, query ? SEARCH_DEBOUNCE_MS : 0);
    return () => clearTimeout(handle);
  }, [value, query]);

  if (value) {
    return (
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <Label className="text-xs">Purview case</Label>
          <p className="truncate text-sm font-medium text-gray-800">{value.name}</p>
        </div>
        <button type="button" onClick={() => onChange(null)} aria-label="Choose another Purview case"
          className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }
  return (
    <div className="space-y-1">
      <Label className="text-xs">Purview case</Label>
      <SearchableDropdown
        ariaLabel="Purview case"
        placeholder="Select a Purview case"
        searchPlaceholder="Search Purview cases…"
        emptyText={query.trim() ? `No Purview cases match “${query.trim()}”.` : 'No Purview cases found.'}
        loading={loading}
        options={results.map((c) => ({ value: c.external_id, label: c.name, hint: c.status }))}
        value={null}
        onSearchChange={setQuery}
        onChange={(id) => {
          const picked = results.find((r) => r.external_id === id);
          if (picked) onChange({ externalId: picked.external_id, name: picked.name });
        }}
      />
      {error && <p className="flex items-center gap-1 text-xs text-rose-600"><AlertCircle className="h-3 w-3" /> {error}</p>}
    </div>
  );
}
